import React, { useCallback, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge'
import { getReorderDestinationIndex } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index'
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import useWindowResize from '@hooks/useWindowResize'
import { useBoardsContext } from '../context/BoardsContext'
import useBoardMutations from '../hooks/useBoardMutations'
import { columnCardsQueryKey } from '../hooks/useColumnCards'
import Column from './Column'

function BoardContent() {
  const boardRef = useRef(null)
  const queryClient = useQueryClient()
  const { currentBoard, selectedBoardId, canEdit, canManage, setIsDragging } = useBoardsContext()
  const { updateCard, updateColumn } = useBoardMutations(selectedBoardId)

  const adjustBoardHeight = useCallback(() => {
    if (boardRef.current) {
      const margin = 50
      boardRef.current.style.height = `${window.innerHeight - boardRef.current.getBoundingClientRect().top - margin}px`
    }
  }, [])

  useWindowResize(adjustBoardHeight)

  const loadedCardsFor = useCallback((columnId) => {
    const data = queryClient.getQueryData([...columnCardsQueryKey(columnId), null])
    return (data?.pages || []).flatMap(p => p.cards || [])
  }, [queryClient])

  const handleDrop = useCallback(({ source, location }) => {
    const dropTargets = location?.current?.dropTargets || []
    if (!dropTargets.length) return

    const type = source.data.type

    if (type === 'card' && canEdit) {
      handleCardDrop(source, dropTargets)
    } else if (type === 'column' && canManage) {
      handleColumnDrop(source, dropTargets)
    }
  }, [currentBoard, canEdit, canManage])

  function handleCardDrop(source, dropTargets) {
    const cardId = source.data.cardId
    const sourceColumnId = source.data.columnId

    if (dropTargets.length === 1) {
      const targetColumnId = dropTargets[0].data.columnId
      updateCard.mutate({
        id: cardId,
        kanban_column_id: targetColumnId,
        row_order_position: 'last',
        _sourceColumnId: sourceColumnId
      })
    } else {
      const [targetCard, targetColumn] = dropTargets
      const targetColumnId = targetColumn.data.columnId
      const closestEdge = extractClosestEdge(targetCard.data)

      const targetCards = loadedCardsFor(targetColumnId)
      const indexOfTarget = targetCards.findIndex(c => c.id === targetCard.data.cardId)

      let destinationIndex
      if (sourceColumnId === targetColumnId) {
        const startIndex = targetCards.findIndex(c => c.id === cardId)
        destinationIndex = getReorderDestinationIndex({
          startIndex,
          indexOfTarget,
          closestEdgeOfTarget: closestEdge,
          axis: 'vertical'
        })
      } else {
        destinationIndex = closestEdge === 'bottom' ? indexOfTarget + 1 : indexOfTarget
      }

      updateCard.mutate({
        id: cardId,
        kanban_column_id: targetColumnId,
        row_order_position: destinationIndex,
        _sourceColumnId: sourceColumnId
      })
    }
  }

  function handleColumnDrop(source, dropTargets) {
    const columnId = source.data.columnId
    if (dropTargets.length < 1) return

    const targetData = dropTargets[0].data
    if (targetData.type !== 'column') return

    const closestEdge = extractClosestEdge(targetData)
    const columns = currentBoard.columns
    const indexOfTarget = columns.findIndex(c => c.id === targetData.columnId)
    const startIndex = columns.findIndex(c => c.id === columnId)

    const destinationIndex = getReorderDestinationIndex({
      startIndex,
      indexOfTarget,
      closestEdgeOfTarget: closestEdge,
      axis: 'horizontal'
    })

    updateColumn.mutate({
      id: columnId,
      row_order_position: destinationIndex
    })
  }

  useEffect(() => monitorForElements({
    onDrop: (args) => { setIsDragging(false); handleDrop(args) },
    onDragStart: () => setIsDragging(true)
  }), [handleDrop, setIsDragging])

  if (!currentBoard) return null

  return (
    <div className="kb" ref={boardRef}>
      {currentBoard.columns.map((column) => (
        <Column key={column.id} column={column} />
      ))}
    </div>
  )
}

export default React.memo(BoardContent)
